import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useStore, type StoreApi } from 'zustand'
import type { AgentFrontendConfig } from '@/config/types'
import { AgentRuntime } from '@/runtime/AgentRuntime'
import { AgentThemeProvider } from '@/theme/AgentThemeProvider'
import { createAgentStore, type AgentStore } from '@/state/useAgentStore'
import { getDefaultModel } from '@/utils/modelSelection'
import { saveLastActiveChatId } from '@/utils/activeChatPersistence'
import { getGuestUserId } from '@/utils/guestUserId'
import { SocketProvider } from './SocketProvider'

interface AgentContextValue {
  runtime: AgentRuntime
  store: StoreApi<AgentStore>
}

const AgentContext = createContext<AgentContextValue | null>(null)

export interface AgentProviderProps {
  config: AgentFrontendConfig
  children: ReactNode
}

export const AgentProvider = ({ config, children }: AgentProviderProps) => {
  const runtimeRef = useRef<AgentRuntime>()
  if (!runtimeRef.current) {
    runtimeRef.current = new AgentRuntime(config)
  }

  // Force provider subtree to rerender when runtime config changes at runtime.
  // The runtime instance is stable by design, so without this, consumers won't pick up updates.
  const [, forceTick] = useState(0)

  useEffect(() => {
    const runtime = runtimeRef.current!
    const onTransportConfigChanged = () => forceTick((x) => x + 1)
    runtime.events.addEventListener('runtime:transport-config-changed', onTransportConfigChanged as EventListener)
    return () => {
      runtime.events.removeEventListener('runtime:transport-config-changed', onTransportConfigChanged as EventListener)
    }
  }, [])

  const storeRef = useRef<StoreApi<AgentStore>>()
  if (!storeRef.current) {
    const defaultContext =
      runtimeRef.current.defaultContextKey ?? runtimeRef.current.config.contextKeys[0]?.key ?? 'default'
    storeRef.current = createAgentStore(defaultContext, () =>
      getDefaultModel(runtimeRef.current!.config.models, runtimeRef.current!.config.transport, defaultContext)
    )
    runtimeRef.current.attachStore(storeRef.current)

    // Initialize model from localStorage or default to "Auto"
    // getDefaultModel returns undefined for "Auto" (uses backend default), or a specific model ID if saved
    const defaultModel = getDefaultModel(
      runtimeRef.current.config.models,
      runtimeRef.current.config.transport,
      defaultContext
    )
    // Always set the model (undefined = "Auto" is the default preselected option)
    storeRef.current.getState().setComposerOptions({ model: defaultModel })
  }

  const value = useMemo<AgentContextValue>(() => ({ runtime: runtimeRef.current!, store: storeRef.current! }), [])

  useEffect(() => {
    const store = storeRef.current
    if (!store) return
    let prevActiveChatId: string | undefined = store.getState().activeChatId
    return store.subscribe(() => {
      const next = store.getState().activeChatId
      if (next === prevActiveChatId) return
      prevActiveChatId = next
      const userId = runtimeRef.current?.config.user?.id ?? getGuestUserId()
      saveLastActiveChatId(userId, next)
    })
  }, [])

  return (
    <AgentContext.Provider value={value}>
      <QueryClientProvider client={value.runtime.queryClient}>
        <AgentThemeProvider value={value.runtime.config.theme}>
          <SocketProvider>{children}</SocketProvider>
        </AgentThemeProvider>
      </QueryClientProvider>
    </AgentContext.Provider>
  )
}

export const useAgentRuntime = () => {
  const ctx = useContext(AgentContext)
  if (!ctx) throw new Error('useAgentRuntime must be used inside <AgentProvider />')
  return ctx.runtime
}

export const useAgentStore = <T,>(selector: (state: AgentStore) => T) => {
  const ctx = useContext(AgentContext)
  if (!ctx) throw new Error('useAgentStore must be used inside <AgentProvider />')
  return useStore(ctx.store, selector)
}
